import { useState, useEffect } from 'react'
import { useNavigate, Link } from 'react-router-dom'
import { useAuthStore } from '@/lib/store'
import { Button } from '@/components/ui/button'
import { Camera, User, Mail, Phone, Shield, Loader2, Crown, Star, Gift, Package, Clock, ChevronRight, Badge } from 'lucide-react'
import { supabase } from '@/lib/supabase'
import { Toast } from '@/components/ui/toast'

const DEFAULT_AVATAR = 'https://images.unsplash.com/photo-1553481187-be93c21490a9?ixlib=rb-1.2.1&ixid=MnwxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8&auto=format&fit=crop&w=300&q=80'

export function ProfilePage() {
  const navigate = useNavigate()
  const user = useAuthStore((state) => state.user)
  const loadUser = useAuthStore((state) => state.loadUser)
  const [loading, setLoading] = useState(false)
  const [showToast, setShowToast] = useState(false)
  const [toastMessage, setToastMessage] = useState('')
  const [toastType, setToastType] = useState<'success' | 'error'>('success')
  const [avatar, setAvatar] = useState(user?.avatar_url || DEFAULT_AVATAR)
  const [formData, setFormData] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    phone_number: user?.phone_number || '',
    role: user?.role || 'buyer',
    status: user?.status || 'active',
    is_verified: user?.is_verified || false
  })
  const [pendingOrders, setPendingOrders] = useState<any[]>([])
  const [loadingOrders, setLoadingOrders] = useState(true)
  const [allOrders, setAllOrders] = useState<any[]>([])

  useEffect(() => {
    if (!user) {
      navigate('/login')
      return
    }

    // Update form data when user data changes
    setFormData({
      full_name: user.full_name || '',
      email: user.email || '',
      phone_number: user.phone_number || '',
      role: user.role || 'buyer',
      status: user.status || 'active',
      is_verified: user.is_verified || false
    })
    setAvatar(user.avatar_url || DEFAULT_AVATAR)
    
    // Fetch pending orders with in-person payment
    fetchPendingOrders()
    
    // Fetch all orders for this user
    fetchAllOrders()
  }, [user, navigate])

  const fetchPendingOrders = async () => {
    if (!user) return
    
    try {
      setLoadingOrders(true)
      
      const { data, error } = await supabase
        .from('orders')
        .select(`
          *,
          product:products (
            title,
            images
          ),
          seller:profiles!seller_id (
            full_name
          )
        `)
        .eq('buyer_id', user.id)
        .eq('payment_type', 'in_person')
        .in('status', ['confirmed', 'paid'])
        .order('created_at', { ascending: false })
        
      if (error) throw error
      
      setPendingOrders(data || [])
    } catch (err) {
      console.error('Error fetching pending orders:', err)
    } finally {
      setLoadingOrders(false)
    }
  }
  
  const fetchAllOrders = async () => {
    if (!user) return
    
    try {
      const { data, error } = await supabase
        .from('orders')
        .select(`
          id,
          status,
          final_price,
          created_at,
          product:products (
            title,
            images
          )
        `)
        .eq('buyer_id', user.id)
        .order('created_at', { ascending: false })
        .limit(5)
        
      if (error) throw error
      
      setAllOrders(data || [])
    } catch (err) {
      console.error('Error fetching all orders:', err)
    }
  }

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!user) return

    try {
      setLoading(true)
      const file = e.target.files?.[0]
      if (!file) return

      // Upload image to Supabase Storage
      const fileExt = file.name.split('.').pop()
      const fileName = `${user.id}-${Math.random()}.${fileExt}`
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(fileName, file)

      if (uploadError) throw uploadError

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(fileName)

      // Update user profile with new avatar URL
      const { error: updateError } = await supabase
        .from('profiles')
        .update({ avatar_url: publicUrl })
        .eq('id', user.id)

      if (updateError) throw updateError

      setAvatar(publicUrl)
      
      // Reload user data to update the global state
      await loadUser()
      
      setToastMessage('Photo de profil mise à jour avec succès')
      setToastType('success')
      setShowToast(true)
    } catch (error) {
      console.error('Error uploading avatar:', error)
      setToastMessage('Erreur lors de la mise à jour de la photo de profil')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!user) return
    
    setLoading(true)

    try {
      const { error } = await supabase
        .from('profiles')
        .update({
          full_name: formData.full_name,
          phone_number: formData.phone_number
        })
        .eq('id', user.id)

      if (error) throw error

      // Reload user data to update the global state
      await loadUser()

      setToastMessage('Profil mis à jour avec succès')
      setToastType('success')
      setShowToast(true)
    } catch (error) {
      console.error('Error updating profile:', error)
      setToastMessage('Erreur lors de la mise à jour du profil')
      setToastType('error')
      setShowToast(true)
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 py-6 sm:py-8 px-4 sm:px-6 lg:px-8">
      {showToast && (
        <Toast
          type={toastType}
          title={toastMessage}
          onClose={() => setShowToast(false)}
          position="top-right"
        />
      )}

      <div className="max-w-4xl mx-auto">
        <div className="bg-white shadow-lg rounded-lg overflow-hidden">
          {/* Membership Banner */}
          <div className="relative h-36 sm:h-48 bg-gradient-to-r from-blue-600 to-blue-800">
            <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1579546929518-9e396f3cc809?auto=format&fit=crop&q=80')] bg-cover bg-center opacity-20"></div>
            
            {/* Main Content */}
            <div className="relative h-full flex flex-col items-center justify-center text-white p-4">
              <Crown className="h-8 w-8 sm:h-12 sm:w-12 mb-2" />
              <h1 className="text-xl sm:text-3xl font-bold tracking-tight text-center">AEM Membership</h1>
              <div className="flex items-center mt-2 space-x-1 sm:space-x-2">
                {[...Array(5)].map((_, i) => (
                  <Star key={i} className="h-4 w-4 sm:h-5 sm:w-5 fill-current" />
                ))}
              </div>
            </div>
            
            {/* Benefits Bar */}
            <div className="absolute bottom-0 left-0 right-0">
              <div className="bg-black/50 backdrop-blur-sm">
                <div className="container mx-auto px-4 py-2">
                  <div className="flex flex-wrap justify-center gap-3 sm:gap-8 text-white text-xs sm:text-sm">
                    <div className="flex items-center whitespace-nowrap">
                      <Gift className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 flex-shrink-0" />
                      <span>Récompenses exclusives</span>
                    </div>
                    <div className="flex items-center whitespace-nowrap">
                      <Star className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 flex-shrink-0" />
                      <span>Service prioritaire</span>
                    </div>
                    <div className="flex items-center whitespace-nowrap">
                      <Crown className="h-3 w-3 sm:h-4 sm:w-4 mr-1 sm:mr-2 flex-shrink-0" />
                      <span>Avantages premium</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Avatar Section */}
          <div className="relative px-4 sm:px-8">
            <div className="absolute -top-12 left-4 sm:left-8">
              <div className="relative">
                <div className="w-20 h-20 sm:w-24 sm:h-24 rounded-full border-4 border-white overflow-hidden bg-gradient-to-br from-primary to-blue-400">
                  <img
                    src={avatar}
                    alt={formData.full_name || 'Profile'}
                    className="w-full h-full object-cover"
                  />
                </div>
                <label
                  htmlFor="avatar-upload"
                  className="absolute bottom-0 right-0 bg-white rounded-full p-2 shadow-lg cursor-pointer hover:bg-gray-50 transition-colors"
                >
                  <Camera className="w-4 h-4 text-gray-600" />
                  <input
                    type="file"
                    id="avatar-upload"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarChange}
                    disabled={loading}
                  />
                </label>
              </div>
            </div>
          </div>

          {/* Profile Content */}
          <div className="pt-16 pb-8 px-4 sm:px-8">
            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start">
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Informations du Profil</h2>
                  <p className="mt-1 text-sm text-gray-500">
                    Mettez à jour vos informations personnelles et gérez vos paramètres.
                  </p>
                </div>
                {formData.is_verified && (
                  <div className="flex items-center bg-blue-50 px-3 py-1 rounded-full mt-2 sm:mt-0">
                    <Badge className="h-4 w-4 text-blue-500 mr-1.5" />
                    <span className="text-sm font-medium text-blue-700">Vérifié</span>
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="full_name" className="block text-sm font-medium text-gray-700">
                    Nom Complet
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="full_name"
                      value={formData.full_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, full_name: e.target.value }))}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary sm:text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                    Email
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      id="email"
                      value={formData.email}
                      disabled
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 sm:text-sm"
                    />
                  </div>
                  <p className="mt-1 text-xs text-gray-500">L'email ne peut pas être modifié</p>
                </div>

                <div>
                  <label htmlFor="phone_number" className="block text-sm font-medium text-gray-700">
                    Numéro de Téléphone
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Phone className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="tel"
                      id="phone_number"
                      value={formData.phone_number}
                      onChange={(e) => setFormData(prev => ({ ...prev, phone_number: e.target.value }))}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:ring-primary focus:border-primary sm:text-sm"
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="role" className="block text-sm font-medium text-gray-700">
                    Type de Compte
                  </label>
                  <div className="mt-1 relative rounded-md shadow-sm">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Shield className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="role"
                      value={
                        formData.role === 'admin' ? 'Administrateur' :
                        formData.role === 'seller' ? 'Vendeur' :
                        formData.role === 'delivery' ? 'Livreur' :
                        'Acheteur'
                      }
                      disabled
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md bg-gray-50 text-gray-500 capitalize sm:text-sm"
                    />
                  </div>
                </div>
              </div>

              <div className="flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={() => navigate('/')}
                  className="w-full sm:w-auto"
                >
                  Annuler
                </Button>
                <Button
                  type="submit"
                  variant="primary"
                  disabled={loading}
                  className="w-full sm:w-auto"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Enregistrement...
                    </>
                  ) : (
                    'Enregistrer les Modifications'
                  )}
                </Button>
              </div>
            </form>
            
            {/* Recent Orders */}
            <div className="mt-10 pt-6 border-t border-gray-200">
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-xl font-bold text-gray-900">Mes commandes récentes</h2>
              </div>
              
              {allOrders.length > 0 ? (
                <div className="space-y-4">
                  {allOrders.map((order) => (
                    <Link 
                      key={order.id} 
                      to={`/orders/tracking/${order.id}`}
                      className="block bg-white border border-gray-200 rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex items-start mb-3 sm:mb-0">
                          <div className="h-16 w-16 flex-shrink-0 bg-gray-100 rounded overflow-hidden">
                            <img
                              src={order.product?.images?.[0] || "https://images.unsplash.com/photo-1523275335684-37898b6baf30"}
                              alt={order.product?.title}
                              className="h-full w-full object-cover"
                            />
                          </div>
                          <div className="ml-4">
                            <h3 className="text-sm font-medium text-gray-900">{order.product?.title}</h3>
                            <p className="mt-1 text-sm text-gray-500">
                              Commandé le {new Date(order.created_at).toLocaleDateString()}
                            </p>
                            <div className="mt-1 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                              {order.status === 'pending' ? 'En attente' :
                               order.status === 'confirmed' ? 'Confirmée' :
                               order.status === 'paid' ? 'Payée' :
                               order.status === 'delivered' ? 'Livrée' : 'Annulée'}
                            </div>
                          </div>
                        </div>
                        <div className="flex flex-col items-start sm:items-end">
                          <p className="text-sm font-medium text-gray-900">${order.final_price.toFixed(2)}</p>
                          <div className="mt-2 flex items-center text-primary">
                            <span className="text-xs">Suivre la commande</span>
                            <ChevronRight className="h-4 w-4 ml-1" />
                          </div>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 bg-gray-50 rounded-lg">
                  <Package className="mx-auto h-12 w-12 text-gray-400" />
                  <h3 className="mt-2 text-sm font-medium text-gray-900">Aucune commande</h3>
                  <p className="mt-1 text-sm text-gray-500">
                    Vous n'avez pas encore passé de commande
                  </p>
                  <Link to="/products">
                    <Button variant="primary" className="mt-4">
                      Parcourir les produits
                    </Button>
                  </Link>
                </div>
              )}
            </div>
            
            {/* Pending In-Person Payment Orders */}
            {pendingOrders.length > 0 && (
              <div className="mt-10 pt-6 border-t border-gray-200">
                <h2 className="text-xl font-bold text-gray-900 mb-4">Commandes en attente de paiement</h2>
                <div className="space-y-4">
                  {pendingOrders.map(order => (
                    <div key={order.id} className="bg-white border border-gray-200 rounded-lg shadow-sm p-4">
                      <div className="flex flex-col sm:flex-row sm:items-start">
                        <div className="h-16 w-16 flex-shrink-0 bg-gray-100 rounded overflow-hidden mb-4 sm:mb-0">
                          <img
                            src={order.product?.images?.[0] || "https://images.unsplash.com/photo-1523275335684-37898b6baf30"}
                            alt={order.product?.title}
                            className="h-full w-full object-cover"
                          />
                        </div>
                        <div className="sm:ml-4 flex-1">
                          <div className="flex flex-col sm:flex-row sm:justify-between">
                            <h3 className="text-sm font-medium text-gray-900">{order.product?.title}</h3>
                            <p className="text-sm font-medium text-gray-900 mt-1 sm:mt-0">${order.final_price.toFixed(2)}</p>
                          </div>
                          <p className="mt-1 text-sm text-gray-500">Vendeur: {order.seller?.full_name}</p>
                          <div className="mt-2 flex items-center">
                            <Clock className="h-4 w-4 text-yellow-500 mr-1" />
                            <span className="text-sm text-yellow-700">
                              {order.status === 'confirmed' ? 'En attente de livraison' : 'En cours de livraison'}
                            </span>
                          </div>
                          
                          {/* Confirmation Code */}
                          <div className="mt-3 bg-blue-50 p-3 rounded-lg">
                            <div className="flex flex-col">
                              <div className="flex items-center mb-2">
                                <Shield className="h-4 w-4 text-blue-500 mr-2 flex-shrink-0" />
                                <span className="text-sm font-medium text-blue-700">Code de confirmation:</span>
                              </div>
                              <div className="bg-blue-100 px-3 py-2 rounded text-center">
                                <span className="text-lg font-bold tracking-wider text-blue-800 break-all">
                                  {order.confirmation_code}
                                </span>
                              </div>
                            </div>
                            <p className="mt-2 text-xs text-blue-600">
                              Présentez ce code au livreur lors de la réception pour confirmer la livraison.
                            </p>
                          </div>
                          
                          {/* Link to tracking page */}
                          <div className="mt-3 text-right">
                            <Link 
                              to={`/orders/tracking/${order.id}`}
                              className="inline-flex items-center text-primary hover:text-primary-dark text-sm"
                            >
                              Suivre la commande
                              <ChevronRight className="h-4 w-4 ml-1" />
                            </Link>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  )
}